// ─── src/pages/AboutLWD.jsx ────────────────────────────────────────────────────
// About LWD, institutional positioning page.
//
// Editorial rhythm (mirrors /apply-for-inclusion):
//   1. Hero (theme)             , compressed manifesto opener
//   2. Foundation (cream LOCKED) ─┐
//   3. Founders   (cream LOCKED) ─┘ magazine spread inside the brochure
//   4. Visibility→Evaluation (theme), Curated Index visualised as a
//                                      5-pillar manuscript folio
//   5. Differentiators (theme)  , five compressed pillars in
//                                  /apply-for-inclusion's voice
//   6. Vision (theme)           , sharper sign-off
//   7. CTA (theme)              , editorial chip links, not pills
//
// Lights toggle: every section except Foundation + Founders flips with
// the user's site-wide preference. The cream beat in the middle is
// permanent, it IS the rhythm break, and matches how
// /apply-for-inclusion locks Hero/Aura/Edit/Form to dark regardless of
// toggle state.
//
// Voice locked: institutional authority, statement-of-standard,
// no SaaS pricing energy, no "designed for the future" filler.

import { useEffect } from "react";
import { ThemeCtx } from "../theme/ThemeContext";
import { getDarkPalette, getLightPalette } from "../theme/tokens";
import { useAura } from "../stores/auraCore";

import HomeNav from "../components/nav/HomeNav";
import SearchIdentity from "../seo/SearchIdentity.jsx";

const GD = "var(--font-heading-primary)";
const NU = "var(--font-body)";

// Cream palette for the magazine-spread sections (Foundation + Founders).
// Same values as /apply-for-inclusion's PALETTE.light so the two pages
// share a single editorial vocabulary. Gold is locked to LIGHT_C.gold so
// the cream sections render correctly even when the page is in dark mode.
const CREAM = {
  bg:      "#faf7f1",
  altBg:   "#f3eee5",
  text:    "#16140f",
  body:    "rgba(22,20,15,0.62)",
  muted:   "rgba(22,20,15,0.55)",
  subtle:  "rgba(22,20,15,0.42)",
  border:  "rgba(22,20,15,0.10)",
  gold:    "#7a5f10",
  goldRule:"rgba(122,95,16,0.45)",
  goldEdge:"rgba(122,95,16,0.30)",
  goldHair:"rgba(122,95,16,0.18)",
};

// ── Differentiators, compressed to match /apply-for-inclusion's Aura
// pillars. Subject-omitted. Read in two seconds, not five.
const DIFFERENTIATORS = [
  "Standards, not stars.",
  "Evaluation, not exposure.",
  "Hallmarks, not ratings.",
  "Direct, not brokered.",
  "Experience, not assumption.",
];

// ── Curated Index dimensions, visualised as a 5-pillar manuscript folio.
// Demonstrates structure instead of describing it.
const CURATED_INDEX_DIMENSIONS = [
  { label: "Quality",        note: "Material & finish" },
  { label: "Experience",     note: "Depth of offering" },
  { label: "Responsiveness", note: "Enquiry handling" },
  { label: "Catering",       note: "Service standards" },
  { label: "Completeness",   note: "Informational integrity" },
];

// ── Theme-responsive accent helpers ───────────────────────────────────────
// Dark and light palettes use different gold hexes (DARK_C.gold #d39a21,
// LIGHT_C.gold #7a5f10), so the gold-alpha rgbas on hairline borders need
// to switch with the theme too. These three helpers cover every hairline
// register on the page.
function getAccents(darkMode) {
  return darkMode
    ? {
        rule: "rgba(211,154,33,0.50)",  // section labels + dividers
        edge: "rgba(211,154,33,0.30)",  // section top borders
        hair: "rgba(211,154,33,0.15)",  // subtle internal hairlines
        folioBorder: "rgba(211,154,33,0.40)",
        folioInner:  "rgba(211,154,33,0.18)",
        chipBorder:  "rgba(211,154,33,0.55)",
        chipHover:   "rgba(211,154,33,0.08)",
      }
    : {
        rule: "rgba(122,95,16,0.45)",
        edge: "rgba(122,95,16,0.30)",
        hair: "rgba(122,95,16,0.18)",
        folioBorder: "rgba(122,95,16,0.40)",
        folioInner:  "rgba(122,95,16,0.18)",
        chipBorder:  "rgba(122,95,16,0.55)",
        chipHover:   "rgba(122,95,16,0.10)",
      };
}

// ── Shared patterns ────────────────────────────────────────────────────────
function SectionLabel({ text, gold, rule }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, justifyContent: "center" }}>
      <div style={{ width: 28, height: 1, background: rule }} />
      <span style={{
        fontFamily: NU, fontSize: 10, letterSpacing: "0.28em",
        textTransform: "uppercase", color: gold, fontWeight: 600,
      }}>
        {text}
      </span>
      <div style={{ width: 28, height: 1, background: rule }} />
    </div>
  );
}

function ChipLink({ onClick, label, gold, border, hover }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 12,
        fontFamily: NU, fontSize: 11, fontWeight: 500,
        letterSpacing: "0.28em", textTransform: "uppercase",
        color: gold,
        background: "transparent",
        border: `1px solid ${border}`,
        outline: "none",
        cursor: "pointer",
        padding: "14px 28px",
        transition: "background 0.25s ease, border-color 0.25s ease",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = hover;
        e.currentTarget.style.borderColor = gold;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.borderColor = border;
      }}
    >
      {label}
      <span aria-hidden style={{
        width: 5, height: 5, background: gold, transform: "rotate(45deg)",
      }} />
    </button>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// Main Component
// ═════════════════════════════════════════════════════════════════════════════
export default function AboutLWD({
  darkMode = true,
  onToggleDark,
  onBack,
  onViewCategory,
  onViewStandard,
  onViewContact,
  onViewPartnership,
  footerNav,
}) {
  const setChatContext = useAura((s) => s.setChatContext);
  const C = darkMode ? getDarkPalette() : getLightPalette();
  const A = getAccents(darkMode);

  useEffect(() => {
    setChatContext?.({ page: "about" });
  }, [setChatContext]);

  return (
    <ThemeCtx.Provider value={C}>
      <SearchIdentity path="/about-us" />
      <div style={{ background: C.black, minHeight: "100vh", transition: "background 0.3s ease" }}>
        <HomeNav
          darkMode={darkMode}
          onToggleDark={onToggleDark}
          onVendorLogin={onBack}
          onNavigateStandard={onViewStandard}
          onNavigateAbout={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          hasHero={false}
        />

        <main>
          {/* ────────────────────────────────────────────────────────────────
              SECTION 1: Hero, compressed manifesto opener (theme).

              H1 swap: "Built on two decades of luxury wedding expertise"
              demoted to a small gold-caps subtitle. The page's strongest
              line, "Luxury requires structure.", promoted from buried
              italic body copy to the H1. Stated fact, not editorial
              whisper. Body trimmed from 6 paragraphs to 3 short stanzas;
              first impression hits in three seconds.
          ──────────────────────────────────────────────────────────────── */}
          <section
            className="lwd-standard-section lwd-standard-hero"
            style={{
              background: C.black,
              // Generous top padding so the "About LWD" eyebrow sits well
              // below the fixed HomeNav. The page is institutional, the
              // first beat needs air, not a tight reveal.
              padding: "clamp(200px, 20vw, 260px) 40px 100px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              textAlign: "center",
              transition: "background 0.3s ease",
            }}
          >
            <div style={{ maxWidth: 800 }}>
              <SectionLabel text="About LWD" gold={C.gold} rule={A.rule} />

              <h1 style={{
                fontFamily: GD, fontSize: "clamp(40px, 5.2vw, 72px)", fontWeight: 400,
                color: C.off, margin: "0 0 22px", lineHeight: 1.08,
                letterSpacing: "-0.01em",
                transition: "color 0.3s ease",
              }}>
                Luxury requires structure.
              </h1>

              <p style={{
                fontFamily: NU, fontSize: 11, fontWeight: 600,
                letterSpacing: "0.32em", textTransform: "uppercase",
                color: C.gold, margin: "0 0 56px",
                transition: "color 0.3s ease",
              }}>
                Built on two decades of luxury wedding expertise.
              </p>

              <div style={{ maxWidth: 580, margin: "0 auto" }}>
                <p style={{
                  fontFamily: NU, fontSize: 17, color: C.off, lineHeight: 1.65,
                  fontWeight: 400, margin: "0 0 28px",
                  transition: "color 0.3s ease",
                }}>
                  Luxury wedding planning has evolved.<br />
                  Discovery has not.
                </p>
                <p style={{
                  fontFamily: NU, fontSize: 16, color: C.grey, lineHeight: 1.85,
                  fontWeight: 300, fontStyle: "italic", margin: "0 0 28px",
                  transition: "color 0.3s ease",
                }}>
                  Listings. Volume. Fragmented information.
                </p>
                <p style={{
                  fontFamily: NU, fontSize: 17, color: C.off, lineHeight: 1.65,
                  fontWeight: 400, margin: 0,
                  transition: "color 0.3s ease",
                }}>
                  Luxury Wedding Directory was created to change that.
                </p>
              </div>

              {/* Gold divider */}
              <div style={{
                width: 40, height: 1, background: C.gold, opacity: 0.5,
                margin: "56px auto 0",
                transition: "background 0.3s ease",
              }} />
            </div>
          </section>

          {/* ────────────────────────────────────────────────────────────────
              SECTION 2: The Foundation, CREAM LOCKED (start of magazine spread).

              Always cream regardless of the page's lights mode. This
              section + Founders below are the page's single rhythm break,
              a magazine "feature spread" inside an otherwise theme-flipping
              document. Same trick as /apply-for-inclusion's locked-dark
              sections (Hero/Aura/Edit/Form), in reverse direction.

              5 Star Weddings parent-company nod pulled out of body prose
              and promoted to a gold-caps masthead eyebrow above the H2.
          ──────────────────────────────────────────────────────────────── */}
          <section
            className="lwd-standard-section"
            style={{
              background: CREAM.bg,
              padding: "100px 60px",
              borderTop: `1px solid ${CREAM.goldEdge}`,
            }}
          >
            <div
              className="about-two-col"
              style={{
                maxWidth: 1100, margin: "0 auto",
                display: "grid", gridTemplateColumns: "1fr 1fr", gap: 80,
                alignItems: "start",
              }}
            >
              {/* Left column */}
              <div>
                <div style={{
                  fontFamily: NU, fontSize: 9, fontWeight: 600,
                  letterSpacing: "0.32em", textTransform: "uppercase",
                  color: CREAM.gold, marginBottom: 22,
                }}>
                  A 5 Star Weddings Company · Two Decades in Luxury
                </div>

                <h2 style={{
                  fontFamily: GD, fontSize: "clamp(28px, 3vw, 44px)", fontWeight: 400,
                  fontStyle: "italic", color: CREAM.gold, margin: "0 0 28px", lineHeight: 1.2,
                }}>
                  The Foundation
                </h2>

                <p style={{
                  fontFamily: NU, fontSize: 15, color: CREAM.body, lineHeight: 1.85,
                  fontWeight: 300, margin: "0 0 16px",
                }}>
                  Years of working closely with exceptional venues, planners,
                  and hospitality brands across multiple continents revealed
                  a clear insight:
                </p>

                <p style={{
                  fontFamily: GD, fontSize: "clamp(20px, 2.2vw, 26px)",
                  fontStyle: "italic", color: CREAM.gold,
                  margin: "28px 0 0", lineHeight: 1.4,
                  letterSpacing: "-0.005em",
                }}>
                  A remarkable venue deserves more than visibility.<br />
                  A couple deserves more than marketing.
                </p>

                <p style={{
                  fontFamily: NU, fontSize: 15, color: CREAM.body, lineHeight: 1.85,
                  fontWeight: 300, margin: "28px 0 0",
                }}>
                  Luxury Wedding Directory represents the next evolution of that experience.
                </p>
              </div>

              {/* Right column, canonical entity anchor */}
              <div style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                paddingLeft: 40,
                borderLeft: `1px solid ${CREAM.goldHair}`,
              }}>
                <div style={{
                  width: 28, height: 1,
                  background: CREAM.gold, opacity: 0.5,
                  marginBottom: 28,
                }} />
                <p style={{
                  fontFamily: NU, fontSize: 14, color: CREAM.body, lineHeight: 1.9,
                  fontWeight: 300, margin: "0 0 20px",
                }}>
                  Luxury Wedding Directory is a curated directory of exceptional
                  wedding venues and vendors across Europe, covering Italy, France,
                  Spain, England and 11 further countries, published by 5 Star
                  Weddings Ltd, a luxury wedding media company established in 2006.
                </p>
                <p style={{
                  fontFamily: NU, fontSize: 14, color: CREAM.body, lineHeight: 1.9,
                  fontWeight: 300, margin: "0 0 20px",
                }}>
                  Couples discover venues through Aura, an AI concierge that
                  understands intent rather than matching keywords. Vendors are
                  evaluated against measurable standards before they appear.
                </p>
                <p style={{
                  fontFamily: NU, fontSize: 14, color: CREAM.body, lineHeight: 1.9,
                  fontWeight: 300, margin: 0,
                }}>
                  Every application is reviewed by the editorial team. The majority
                  are declined.
                </p>
                <div style={{
                  width: 28, height: 1,
                  background: CREAM.gold, opacity: 0.5,
                  marginTop: 28,
                }} />
              </div>
            </div>
          </section>

          {/* ────────────────────────────────────────────────────────────────
              SECTION 3: The Founders, CREAM LOCKED (continues spread).

              Founders made present, not just named. Attributed italic
              pull quote with magazine-epigraph treatment (gold rules
              above and below, attribution in tiny gold caps), same
              vocabulary as /apply-for-inclusion's Authority Statement.
          ──────────────────────────────────────────────────────────────── */}
          <section
            className="lwd-standard-section"
            style={{
              background: CREAM.altBg,
              padding: "100px 60px",
            }}
          >
            <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
              <h2 style={{
                fontFamily: GD, fontSize: "clamp(28px, 3vw, 44px)", fontWeight: 400,
                fontStyle: "italic", color: CREAM.gold, margin: "0 0 28px", lineHeight: 1.2,
              }}>
                The Founders
              </h2>

              <p style={{
                fontFamily: NU, fontSize: 15, color: CREAM.body, lineHeight: 1.85,
                fontWeight: 300, margin: "0 0 16px",
              }}>
                Yasmine and Taiwo founded 5 Star Weddings Ltd in 2006, building
                one of the longest-standing luxury wedding media businesses in
                Europe. Their work spans venue partnerships, editorial curation,
                digital discovery strategy, and hospitality brand positioning
                across the UK, Italy, France, Spain and beyond.
              </p>
              <p style={{
                fontFamily: NU, fontSize: 15, color: CREAM.body, lineHeight: 1.85,
                fontWeight: 300, margin: 0,
              }}>
                Luxury Wedding Directory is the next chapter, built on two
                decades of direct observation: what exceptional venues need to
                be discovered properly, and what discerning couples need to
                make the right decision.
              </p>

              {/* Founder pull quote, magazine epigraph */}
              <div style={{ marginTop: "clamp(56px, 7vw, 88px)" }}>
                <div aria-hidden style={{
                  width: "clamp(80px, 14vw, 180px)",
                  height: 1,
                  margin: "0 auto 36px",
                  background: `linear-gradient(to right, transparent, ${CREAM.gold}, transparent)`,
                }} />
                <p style={{
                  fontFamily: GD,
                  fontStyle: "italic",
                  fontSize: "clamp(22px, 2.6vw, 34px)",
                  fontWeight: 400,
                  color: CREAM.text,
                  lineHeight: 1.4,
                  letterSpacing: "-0.005em",
                  margin: "0 auto",
                  maxWidth: 680,
                }}>
                  For twenty years we worked with the world’s finest venues.
                  We watched the work evolve. We watched discovery stand still.
                  Luxury Wedding Directory is our answer to that gap.
                </p>
                <p style={{
                  fontFamily: NU, fontSize: 9, fontWeight: 600,
                  letterSpacing: "0.32em", textTransform: "uppercase",
                  color: CREAM.gold, marginTop: 28, marginBottom: 0,
                }}>
                  Yasmine &amp; Taiwo, Founders
                </p>
                <div aria-hidden style={{
                  width: "clamp(80px, 14vw, 180px)",
                  height: 1,
                  margin: "36px auto 0",
                  background: `linear-gradient(to right, transparent, ${CREAM.gold}, transparent)`,
                }} />
              </div>

              <p style={{
                fontFamily: NU, fontSize: 15, color: CREAM.text, lineHeight: 1.85,
                fontWeight: 400, margin: "clamp(56px, 6vw, 80px) 0 0",
              }}>
                Luxury Wedding Directory reflects a deliberate progression.
                Not simply a platform to showcase venues, but a system
                designed to elevate how luxury weddings are discovered.
                Built by people who have spent two decades inside that world.
              </p>
            </div>
          </section>

          {/* ────────────────────────────────────────────────────────────────
              SECTION 4: From Visibility to Evaluation, theme.

              Curated Index promoted from a list inside a paragraph to a
              5-pillar manuscript folio: gold-caps labels, hairline
              dividers between, short descriptors. Structure demonstrated,
              not described.
          ──────────────────────────────────────────────────────────────── */}
          <section
            className="lwd-standard-section"
            style={{
              background: C.black,
              padding: "120px 60px",
              borderTop: `1px solid ${A.hair}`,
              textAlign: "center",
              transition: "background 0.3s ease, border-color 0.3s ease",
            }}
          >
            <div style={{ maxWidth: 1000, margin: "0 auto" }}>
              <h2 style={{
                fontFamily: GD, fontSize: "clamp(28px, 3vw, 44px)", fontWeight: 400,
                color: C.off, margin: "0 0 32px", lineHeight: 1.2,
                transition: "color 0.3s ease",
              }}>
                From visibility to evaluation.
              </h2>
              <p style={{
                fontFamily: NU, fontSize: 15, color: C.grey, lineHeight: 1.85,
                fontWeight: 300, margin: "0 auto",
                maxWidth: 640,
                transition: "color 0.3s ease",
              }}>
                Traditional platforms prioritise exposure. Luxury Wedding
                Directory prioritises evaluation. At the core of the Directory
                is the Curated Index, a structured framework assessing venues
                across measurable dimensions:
              </p>

              {/* Curated Index folio */}
              <div style={{
                marginTop: "clamp(48px, 6vw, 72px)",
                marginBottom: "clamp(48px, 6vw, 72px)",
                display: "grid",
                gridTemplateColumns: "repeat(5, 1fr)",
                gap: 0,
                borderTop: `1px solid ${A.folioBorder}`,
                borderBottom: `1px solid ${A.folioBorder}`,
                padding: "clamp(28px, 3.5vw, 44px) 0",
                transition: "border-color 0.3s ease",
              }}>
                {CURATED_INDEX_DIMENSIONS.map((d, i) => (
                  <div key={d.label} style={{
                    padding: "0 clamp(10px, 1.4vw, 22px)",
                    borderLeft: i === 0 ? "none" : `1px solid ${A.folioInner}`,
                    textAlign: "center",
                    minWidth: 0,
                    transition: "border-color 0.3s ease",
                  }}>
                    <div aria-hidden style={{
                      width: 5, height: 5, background: C.gold,
                      transform: "rotate(45deg)",
                      margin: "0 auto 14px",
                      transition: "background 0.3s ease",
                    }} />
                    <div style={{
                      fontFamily: NU, fontSize: 10, fontWeight: 600,
                      letterSpacing: "0.26em", textTransform: "uppercase",
                      color: C.gold, marginBottom: 8, lineHeight: 1.3,
                      transition: "color 0.3s ease",
                    }}>
                      {d.label}
                    </div>
                    <div style={{
                      fontFamily: NU, fontSize: 11.5, fontWeight: 300,
                      color: C.grey, lineHeight: 1.45,
                      letterSpacing: "0.01em",
                      transition: "color 0.3s ease",
                    }}>
                      {d.note}
                    </div>
                  </div>
                ))}
              </div>

              <p style={{
                fontFamily: NU, fontSize: 15, color: C.grey, lineHeight: 1.85,
                fontWeight: 300, margin: "0 0 8px",
                transition: "color 0.3s ease",
              }}>
                This is not about popularity. It is about performance.
              </p>
              <p style={{
                fontFamily: NU, fontSize: 15, color: C.grey, lineHeight: 1.85,
                fontWeight: 300, margin: "0 0 8px",
                transition: "color 0.3s ease",
              }}>
                This is not about sponsorship. It is about standards.
              </p>
              <p style={{
                fontFamily: NU, fontSize: 15, color: C.off, lineHeight: 1.85,
                fontWeight: 400, margin: 0,
                transition: "color 0.3s ease",
              }}>
                The editorial decision is not negotiable.
              </p>
            </div>
          </section>

          {/* ────────────────────────────────────────────────────────────────
              SECTION 5: What Makes LWD Different, compressed pillars (theme).
          ──────────────────────────────────────────────────────────────── */}
          <section
            className="lwd-standard-section"
            style={{
              background: C.black,
              padding: "100px 60px",
              borderTop: `1px solid ${A.hair}`,
              transition: "background 0.3s ease, border-color 0.3s ease",
            }}
          >
            <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
              <h2 style={{
                fontFamily: GD, fontSize: "clamp(28px, 3vw, 44px)", fontWeight: 400,
                color: C.off, margin: "0 0 56px", lineHeight: 1.2,
                transition: "color 0.3s ease",
              }}>
                What makes Luxury Wedding Directory different
              </h2>

              <div style={{
                display: "flex", flexDirection: "column",
                gap: "clamp(14px, 1.6vw, 22px)",
                alignItems: "center",
                maxWidth: 720, margin: "0 auto",
              }}>
                {DIFFERENTIATORS.map((line, i) => (
                  <p key={i} style={{
                    fontFamily: GD,
                    fontSize: "clamp(20px, 2.2vw, 30px)",
                    fontStyle: "italic",
                    fontWeight: 400,
                    color: C.off,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.35,
                    margin: 0,
                    transition: "color 0.3s ease",
                  }}>
                    {line}
                  </p>
                ))}
              </div>

              <p style={{
                fontFamily: NU, fontSize: 13, color: C.grey, lineHeight: 1.85,
                fontWeight: 300, margin: "clamp(48px, 5vw, 64px) 0 0",
                fontStyle: "italic",
                letterSpacing: "0.02em",
                transition: "color 0.3s ease",
              }}>
                These principles shape how the platform operates.
              </p>
            </div>
          </section>

          {/* ────────────────────────────────────────────────────────────────
              SECTION 6: The Vision, sharper sign-off (theme).
          ──────────────────────────────────────────────────────────────── */}
          <section
            className="lwd-standard-section"
            style={{
              background: C.dark,
              padding: "100px 60px",
              borderTop: `1px solid ${A.hair}`,
              textAlign: "center",
              transition: "background 0.3s ease, border-color 0.3s ease",
            }}
          >
            <div style={{ maxWidth: 720, margin: "0 auto" }}>
              <h2 style={{
                fontFamily: GD, fontSize: "clamp(28px, 3vw, 44px)", fontWeight: 400,
                fontStyle: "italic", color: C.gold, margin: "0 0 32px", lineHeight: 1.2,
                transition: "color 0.3s ease",
              }}>
                The Vision
              </h2>
              <p style={{
                fontFamily: NU, fontSize: 15, color: C.grey, lineHeight: 1.85,
                fontWeight: 300, margin: "0 0 24px",
                transition: "color 0.3s ease",
              }}>
                From Lake Como villas and Provençal estates to clifftop
                celebrations on the Amalfi Coast and country houses in the
                Cotswolds, the venues here are listed because they met the
                standard. That is the only path in.
              </p>
              <p style={{
                fontFamily: NU, fontSize: 15, color: C.off, lineHeight: 1.85,
                fontWeight: 400, margin: "0 0 16px",
                transition: "color 0.3s ease",
              }}>
                The future of wedding discovery belongs to trusted evaluation,
                not volume.
              </p>
              <p style={{
                fontFamily: NU, fontSize: 15, color: C.grey, lineHeight: 1.85,
                fontWeight: 300, margin: "0 0 72px",
                transition: "color 0.3s ease",
              }}>
                Luxury Wedding Directory exists to define that standard.
                A directory built not for the volume of the market,
                but for the standard it deserves.
              </p>

              <p style={{
                fontFamily: GD, fontSize: "clamp(32px, 4vw, 56px)", fontStyle: "italic",
                color: C.gold, margin: 0, lineHeight: 1.2,
                letterSpacing: "-0.01em",
                transition: "color 0.3s ease",
              }}>
                A standard, by design.
              </p>
            </div>
          </section>

          {/* ────────────────────────────────────────────────────────────────
              SECTION 7: CTA, editorial chip links (theme).
          ──────────────────────────────────────────────────────────────── */}
          <section
            className="lwd-standard-section"
            style={{
              background: C.black,
              padding: "100px 60px",
              borderTop: `1px solid ${A.hair}`,
              textAlign: "center",
              transition: "background 0.3s ease, border-color 0.3s ease",
            }}
          >
            <div
              className="lwd-standard-ctas"
              style={{
                display: "flex", gap: "clamp(16px, 2.4vw, 28px)",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <ChipLink
                onClick={onViewCategory}
                label="Discover the Venues"
                gold={C.gold}
                border={A.chipBorder}
                hover={A.chipHover}
              />
              <ChipLink
                onClick={onViewStandard}
                label="Read the LWD Standard"
                gold={C.gold}
                border={A.chipBorder}
                hover={A.chipHover}
              />
            </div>
          </section>
        </main>

      </div>
    </ThemeCtx.Provider>
  );
}
